import React, { useState, useEffect, useCallback } from 'react';
import {
    X,
    RotateCcw,
    GraduationCap,
    Sparkles,
    Camera,
    Package,
    Tag,
    Lightbulb,
    Target,
    MessageSquare,
    Heart,
    Users,
    Zap,
    TrendingUp,
    Lock,
    Unlock
} from 'lucide-react';

const ICON_MAP = {
    GraduationCap,
    Sparkles,
    Camera,
    Package,
    Tag,
    Lightbulb,
    Target,
    MessageSquare,
    Heart,
    Users,
    Zap,
    TrendingUp
};

/**
 * PillarWeightsModal
 * Allows adjusting all pillar weights simultaneously with linked sliders
 * that always total 100%.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {function} props.onClose
 * @param {function} props.onSave - Called with array of { id, weight } when saved
 * @param {Array} props.pillars - Active pillars array
 * @param {boolean} props.loading - Whether save is in progress
 */
const PillarWeightsModal = ({ isOpen, onClose, onSave, pillars = [], loading = false }) => {
    const [weights, setWeights] = useState({});
    const [lockedPillars, setLockedPillars] = useState({});

    // Initialize weights from pillars
    useEffect(() => {
        if (isOpen && pillars.length > 0) {
            const initial = {};
            pillars.forEach(p => {
                initial[p.id] = p.frequency?.currentWeight || 0;
            });
            // Normalize if they don't sum to 100
            const total = Object.values(initial).reduce((s, v) => s + v, 0);
            if (total !== 100 && total > 0) {
                const scale = 100 / total;
                let distributed = 0;
                const ids = Object.keys(initial);
                ids.forEach((id, i) => {
                    if (i === ids.length - 1) {
                        initial[id] = 100 - distributed;
                    } else {
                        initial[id] = Math.round(initial[id] * scale);
                        distributed += initial[id];
                    }
                });
            } else if (total === 0) {
                // All zero - distribute evenly
                const base = Math.floor(100 / pillars.length);
                const rem = 100 - base * pillars.length;
                pillars.forEach((p, i) => {
                    initial[p.id] = base + (i < rem ? 1 : 0);
                });
            }
            setWeights(initial);
            setLockedPillars({});
        }
    }, [isOpen, pillars]);

    const totalWeight = Object.values(weights).reduce((s, v) => s + v, 0);

    /**
     * When a slider changes, redistribute the difference proportionally
     * among all other unlocked pillars so the total always stays at 100%.
     */
    const handleWeightChange = useCallback((pillarId, rawNewValue) => {
        setWeights(prev => {
            const next = { ...prev };

            // Locked total (pillars we can't touch, excluding the one being dragged)
            const lockedTotal = Object.keys(prev)
                .filter(id => id !== pillarId && lockedPillars[id])
                .reduce((s, id) => s + prev[id], 0);

            // Adjustable pillars (unlocked, not the one being dragged)
            const adjustable = Object.keys(prev).filter(
                id => id !== pillarId && !lockedPillars[id]
            );

            // Clamp the new value: can't exceed what's left after locked pillars
            const maxForThis = 100 - lockedTotal;
            // Must leave at least 0 for each adjustable pillar (floor is 0 per pillar)
            const newValue = Math.max(0, Math.min(rawNewValue, maxForThis));

            if (newValue === prev[pillarId]) return prev;

            next[pillarId] = newValue;

            // Budget that adjustable pillars must share
            const adjustableBudget = 100 - lockedTotal - newValue;

            if (adjustable.length === 0) return next;

            const adjustableOldTotal = adjustable.reduce((s, id) => s + prev[id], 0);

            if (adjustableOldTotal === 0 || adjustableBudget <= 0) {
                // Can't scale proportionally — distribute evenly (or all zeros)
                const budget = Math.max(0, adjustableBudget);
                const base = Math.floor(budget / adjustable.length);
                const rem = budget - base * adjustable.length;
                adjustable.forEach((id, i) => {
                    next[id] = base + (i < rem ? 1 : 0);
                });
                return next;
            }

            // Scale proportionally based on each pillar's share of the old total
            const scale = adjustableBudget / adjustableOldTotal;
            let distributed = 0;

            adjustable.forEach((id, i) => {
                if (i === adjustable.length - 1) {
                    // Last one gets the exact remainder to guarantee 100% total
                    next[id] = Math.max(0, adjustableBudget - distributed);
                } else {
                    next[id] = Math.max(0, Math.round(prev[id] * scale));
                    distributed += next[id];
                }
            });

            return next;
        });
    }, [lockedPillars]);

    const toggleLock = (pillarId) => {
        setLockedPillars(prev => ({
            ...prev,
            [pillarId]: !prev[pillarId]
        }));
    };

    const distributeEvenly = () => {
        const ids = pillars.map(p => p.id);
        const lockedIds = ids.filter(id => lockedPillars[id]);
        const unlockedIds = ids.filter(id => !lockedPillars[id]);

        if (unlockedIds.length === 0) return;

        const lockedTotal = lockedIds.reduce((s, id) => s + (weights[id] || 0), 0);
        const remaining = 100 - lockedTotal;
        const base = Math.floor(remaining / unlockedIds.length);
        const rem = remaining - base * unlockedIds.length;

        const next = { ...weights };
        unlockedIds.forEach((id, i) => {
            next[id] = base + (i < rem ? 1 : 0);
        });
        setWeights(next);
    };

    const handleSave = () => {
        const result = pillars.map(p => ({
            id: p.id,
            weight: weights[p.id] || 0
        }));
        onSave(result);
    };

    if (!isOpen) return null;

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999
            }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div
                className="card"
                style={{
                    width: '100%',
                    maxWidth: '560px',
                    margin: 'var(--space-4)',
                    maxHeight: '80vh',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 'var(--space-4)'
                }}>
                    <div>
                        <h3 style={{ margin: 0 }}>Adjust Pillar Weights</h3>
                        <p style={{
                            margin: 'var(--space-1) 0 0 0',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-secondary)'
                        }}>
                            Drag sliders to adjust. Other unlocked pillars rebalance automatically.
                        </p>
                    </div>
                    <button onClick={onClose} className="btn btn-ghost" style={{ padding: 'var(--space-2)' }}>
                        <X size={20} />
                    </button>
                </div>

                {/* Total indicator */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-2) var(--space-3)',
                    background: totalWeight === 100 ? 'var(--success-50)' : 'var(--warning-50)',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 'var(--space-4)'
                }}>
                    <span style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        color: totalWeight === 100 ? 'var(--success-700)' : 'var(--warning-700)'
                    }}>
                        Total: {totalWeight}%
                    </span>
                    <button
                        type="button"
                        onClick={distributeEvenly}
                        className="btn btn-ghost btn-sm"
                        style={{ gap: 'var(--space-1)' }}
                    >
                        <RotateCcw size={14} />
                        Even Split
                    </button>
                </div>

                {/* Pillar sliders */}
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-3)'
                }}>
                    {pillars.map(pillar => {
                        const IconComp = ICON_MAP[pillar.icon] || Lightbulb;
                        const weight = weights[pillar.id] || 0;
                        const isLocked = lockedPillars[pillar.id] || false;

                        return (
                            <div
                                key={pillar.id}
                                style={{
                                    padding: 'var(--space-3)',
                                    background: 'var(--color-bg-secondary)',
                                    borderRadius: 'var(--radius-md)',
                                    borderLeft: `4px solid ${pillar.color || 'var(--color-primary)'}`,
                                    opacity: isLocked ? 0.7 : 1
                                }}
                            >
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: 'var(--space-2)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                        <div style={{
                                            width: '28px',
                                            height: '28px',
                                            borderRadius: 'var(--radius-sm)',
                                            background: `${pillar.color}15`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <IconComp size={14} style={{ color: pillar.color }} />
                                        </div>
                                        <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
                                            {pillar.name}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                        <span style={{
                                            fontSize: 'var(--font-size-lg)',
                                            fontWeight: 700,
                                            color: pillar.color || 'var(--color-primary)',
                                            minWidth: '45px',
                                            textAlign: 'right'
                                        }}>
                                            {weight}%
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => toggleLock(pillar.id)}
                                            className="btn btn-ghost btn-sm"
                                            style={{ padding: '4px' }}
                                            title={isLocked ? 'Unlock this pillar' : 'Lock this pillar (prevents auto-adjustment)'}
                                        >
                                            {isLocked ? (
                                                <Lock size={14} style={{ color: 'var(--warning-600)' }} />
                                            ) : (
                                                <Unlock size={14} style={{ color: 'var(--color-text-tertiary)' }} />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Slider */}
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={weight}
                                        onChange={(e) => handleWeightChange(pillar.id, parseInt(e.target.value))}
                                        disabled={isLocked}
                                        style={{
                                            width: '100%',
                                            accentColor: pillar.color || 'var(--color-primary)',
                                            cursor: isLocked ? 'not-allowed' : 'pointer'
                                        }}
                                    />
                                    {/* Visual bar underneath */}
                                    <div style={{
                                        height: '4px',
                                        background: 'var(--color-bg-tertiary)',
                                        borderRadius: 'var(--radius-full)',
                                        marginTop: '-2px',
                                        overflow: 'hidden',
                                        pointerEvents: 'none'
                                    }}>
                                        <div style={{
                                            width: `${weight}%`,
                                            height: '100%',
                                            background: pillar.color || 'var(--color-primary)',
                                            borderRadius: 'var(--radius-full)',
                                            transition: 'width 0.1s ease'
                                        }} />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 'var(--space-2)',
                    marginTop: 'var(--space-4)',
                    paddingTop: 'var(--space-4)',
                    borderTop: '1px solid var(--color-border)'
                }}>
                    <button onClick={onClose} className="btn btn-ghost" disabled={loading}>
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="btn btn-primary"
                        disabled={loading || totalWeight !== 100}
                    >
                        {loading ? 'Saving...' : 'Save Weights'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PillarWeightsModal;
